import * as amplitude from "@amplitude/analytics-browser"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import { Trans } from "@lingui/react/macro"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { useMolecule } from "bunshi/react"
import { type RefObject, useEffect, useMemo, useRef, useState } from "react"
import { useAccount, useSwitchChain } from "wagmi"
import { BlockchainsMoleculeReatom } from "../blockchains/blockchain-molecule.reatom"
import {
  getEvmSignerState,
  shouldOfferEvmWalletLink,
} from "../wallet-connect/connected-addresses"
import {
  type EvmReconnectResult,
  WalletConnectMoleculeReatom,
} from "../wallet-connect/store.reatom"
import { ViewBlockchainMolecule } from "./anaconda-sdk-scope"
import { EvmSdkContainerMoleculeReatom } from "./blockchains/evm-sdk-container-molecule.reatom"
import { AppToast } from "./toasts/app-toast"

export function useSwitchEvmChain<T>(
  blockchain: Blockchain,
  onSuccess: (args: T) => void,
) {
  const callArgs = useRef<T | undefined>(undefined)
  const onSuccessRef = useRef(onSuccess)
  // Keep ref updated with latest callback to avoid stale closures
  onSuccessRef.current = onSuccess
  const [switchRequested, setSwitchRequested] = useState(false)
  const [targetBlockchain, setTargetBlockchain] =
    useState<Blockchain>(blockchain)

  const { evmAddress, hasEvmWallet, hasSolanaWallet, linkAdditionalWallet } =
    useMolecule(WalletConnectMoleculeReatom)
  const needsEvmWalletLink = shouldOfferEvmWalletLink({
    hasSolanaWallet: hasSolanaWallet(),
    hasEvmWallet: hasEvmWallet(),
  })
  const { linkEvm, reconnectEvm } = linkAdditionalWallet()
  const { address: signerAddress, isConnecting, isReconnecting } = useAccount()
  const signerState = getEvmSignerState({
    profileAddress: evmAddress(),
    signerAddress,
  })

  useRegularWalletEvmSwitch(
    callArgs,
    onSuccessRef,
    switchRequested,
    setSwitchRequested,
    targetBlockchain,
    onSuccess,
  )

  return useMemo(
    () => ({
      /** Returns false when the action did not start, so callers can settle. */
      action: (args: T): boolean => {
        // Solana-only users have no EVM wallet client, so the switch below
        // would wait forever. Ask them to link an EVM wallet instead.
        if (needsEvmWalletLink) {
          linkEvm()
          return false
        }

        // A locked or disconnected wallet keeps the Profile address but has
        // no signer, so the switch below would also wait forever.
        if (signerState === "mismatch") {
          showSignerUnavailableToast("mismatch")
          return false
        }
        if (signerState === "missing") {
          showSignerUnavailableToast(
            isConnecting || isReconnecting ? "restoring" : reconnectEvm(),
          )
          return false
        }

        callArgs.current = args

        if (
          typeof args === "object" &&
          args !== null &&
          Object.prototype.hasOwnProperty.call(args, "blockchain") &&
          (args as Record<string, unknown>).blockchain !== targetBlockchain
        ) {
          const blockchain = (args as Record<string, unknown>)
            .blockchain as Blockchain
          setTargetBlockchain(blockchain)
        }
        setSwitchRequested(true)
        return true
      },
      switchInProgress: switchRequested,
    }),
    [
      switchRequested,
      targetBlockchain,
      needsEvmWalletLink,
      linkEvm,
      signerState,
      isConnecting,
      isReconnecting,
      reconnectEvm,
    ],
  )
}

function showSignerUnavailableToast(reason: "mismatch" | EvmReconnectResult) {
  sonnerToast.dismiss()
  sonnerToast.custom(
    (id) => {
      switch (reason) {
        case "mismatch":
          return (
            <AppToast.Error
              id={id}
              title="Wrong wallet account"
              main={<Trans>Wrong wallet account</Trans>}
              second={
                <Trans>
                  Switch your wallet to the account you logged in with
                </Trans>
              }
            />
          )
        case "prompted":
          return (
            <AppToast.Error
              id={id}
              title="Wallet locked"
              main={<Trans>Wallet locked</Trans>}
              second={
                <Trans>Unlock your wallet and connect it to continue</Trans>
              }
            />
          )
        case "restoring":
          return (
            <AppToast.Error
              id={id}
              title="Wallet is connecting"
              main={<Trans>Wallet is connecting</Trans>}
              second={<Trans>Try again in a moment</Trans>}
            />
          )
        case "unavailable":
          return (
            <AppToast.Error
              id={id}
              title="Wallet not connected"
              main={<Trans>Wallet not connected</Trans>}
              second={<Trans>Log out and log in again to continue</Trans>}
            />
          )
      }
    },
    { duration: 6000 },
  )
}

function useRegularWalletEvmSwitch<T>(
  callArgs: RefObject<T | undefined>,
  onSuccessRef: RefObject<(args: T) => void>,
  switchRequested: boolean,
  setSwitchRequested: (value: boolean) => void,
  blockchain: Blockchain,
  onSuccess: (args: T) => void,
) {
  const { isConnected } = useAccount()

  const { switchChain } = useSwitchChain()

  // Track if we've already executed for this switch request
  const hasExecutedRef = useRef(false)

  // Reset execution flag when switch is requested
  useEffect(() => {
    if (switchRequested) {
      hasExecutedRef.current = false
    }
  }, [switchRequested])

  const { walletBlockchainNetworkId: walletBlockchainNetworkIdAtom } =
    useMolecule(WalletConnectMoleculeReatom)
  const { reatomBlockchainChainName, reatomChainId } = useMolecule(
    BlockchainsMoleculeReatom,
  )
  const { sdkContainerAtom } = useMolecule(EvmSdkContainerMoleculeReatom)

  // Get view chain ID directly from the blockchain parameter (target chain for operation)
  // This works for both NFTs and Drops regardless of URL path (e.g., /all)
  const viewChainId = reatomChainId()(blockchain)
  // Get wallet chain ID (global - actual wallet state)
  const walletChainId = walletBlockchainNetworkIdAtom()

  // Get SDK container (read outside useEffect so it's in scope for dependencies)
  const sdkContainer = sdkContainerAtom()

  const expectedChainName = reatomBlockchainChainName()(blockchain)

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const sdkInitialized = walletChainId !== undefined

    if (!switchRequested || !sdkInitialized) {
      return
    }
    // Simplified: Get wallet chain directly from SDK container to avoid race conditions
    ;(async () => {
      try {
        if (viewChainId === undefined) {
          return
        }

        // The SDK container exists only while the wallet is on a chain the
        // app has a config for. Without it the wallet is on an unsupported
        // chain, so skip the readiness check and ask the wallet to switch.
        if (sdkContainer) {
          const { ReatomTokens } = await import(
            "@rarible_int/anaconda_sdk/ethereum"
          )
          const sdkWalletChainIdAtom = sdkContainer.resolve(
            ReatomTokens.SDK_WALLET_CHAIN_ID_ATOM,
          )
          const sdkWalletChainId = await sdkWalletChainIdAtom()

          // Check if SDK container's wallet chainId matches the view chain (transaction ready)
          if (sdkWalletChainId === viewChainId) {
            // Guard against multiple executions during re-renders
            if (hasExecutedRef.current) {
              return
            }

            hasExecutedRef.current = true
            onSuccessRef.current(callArgs.current as T)
            setSwitchRequested(false)
            sonnerToast.dismiss()
            amplitude.track("Switch network completed", {
              chainId: viewChainId,
            })
            callArgs.current = undefined
            return
          }
        }

        // If chains don't match, trigger SDK recreation by updating the wallet chain atom
        if (viewChainId !== walletChainId) {
          // Type assertion: viewChainId is guaranteed to be a number inside this block
          const targetChainId = viewChainId as number

          // Now safe to do UI updates (amplitude, toast) that might trigger re-renders
          try {
            amplitude.track("Switch network initiated", {
              chainId: targetChainId,
            })
            switchChain(
              { chainId: targetChainId },
              {
                // A rejected or failed switch ends the request, so the caller
                // is not left pending and the next click can ask again.
                // switchChain is a TanStack mutation: only the latest call's
                // callbacks run, so a superseded prompt cannot cancel a newer
                // request.
                onError: () => {
                  setSwitchRequested(false)
                  callArgs.current = undefined
                },
              },
            )
          } catch (e) {
            console.warn("Failed to track amplitude event:", e)
          }

          try {
            sonnerToast.dismiss()
            sonnerToast.custom(
              (id) => (
                <AppToast.Pending
                  id={id}
                  title="Network switch"
                  main={<Trans>Please switch to {expectedChainName}</Trans>}
                  second={null}
                />
              ),
              {
                duration: 3000,
              },
            )
          } catch (e) {
            console.warn("Failed to show toast:", e)
          }
        }
      } catch (error) {
        console.error("❌ [SWITCH_HOOK] Error in switch logic:", error)
      }
    })()
  }, [
    switchRequested,
    viewChainId,
    walletChainId,
    sdkContainer,
    expectedChainName,
    switchChain,
    isConnected,
  ])
}
